import { CheckSquare, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LibraryCategoryPicker } from "@/components/library/LibraryCategoryPicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { libraryItemStatusOptions, type LibraryItemStatus } from "@/lib/library";
import type { LibraryCategory, LibraryCategoryColorKey } from "@/lib/libraryCategories";

type LibraryBulkBarProps = {
  allSelected: boolean;
  selectedCount: number;
  categories: LibraryCategory[];
  disabled?: boolean;
  onToggleSelectAll: () => void;
  onClear: () => void;
  onAssignCategories: (categoryIds: string[]) => void;
  onRemoveCategories: (categoryIds: string[]) => void;
  onSetStatus: (status: LibraryItemStatus) => void;
  onDelete: () => void;
  onCreateCategory: (values: { name: string; color: LibraryCategoryColorKey }) => Promise<LibraryCategory>;
};

export const LibraryBulkBar = ({
  allSelected,
  selectedCount,
  categories,
  disabled = false,
  onToggleSelectAll,
  onClear,
  onAssignCategories,
  onRemoveCategories,
  onSetStatus,
  onDelete,
  onCreateCategory,
}: LibraryBulkBarProps) => {
  return (
    <div className="sticky top-0 z-20 rounded-md border border-border bg-background/95 p-4 shadow-soft backdrop-blur space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onToggleSelectAll} className="flex items-center gap-2 text-sm font-medium text-foreground" disabled={disabled}>
            <Checkbox checked={allSelected} />
            <span>{selectedCount} source{selectedCount === 1 ? "" : "s"} selected</span>
          </button>
          <p className="text-xs text-muted-foreground">Update status or categories in bulk, or remove selected records.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onToggleSelectAll} disabled={disabled || selectedCount === 0}>
            <CheckSquare className="h-4 w-4" /> {allSelected ? "Unselect all" : "Select all"}
          </Button>
          <Button variant="outline" size="sm" onClick={onClear} disabled={disabled}><X className="h-4 w-4" /> Exit selection</Button>
          <Button variant="outline" size="sm" onClick={onDelete} disabled={disabled}><Trash2 className="h-4 w-4" /> Delete selected</Button>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Set status</p>
          <Select
            disabled={disabled || selectedCount === 0}
            value=""
            onValueChange={(value) => onSetStatus(value as LibraryItemStatus)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Set status for selected…" />
            </SelectTrigger>
            <SelectContent>
              {libraryItemStatusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Assign categories</p>
          <LibraryCategoryPicker
            categories={categories}
            value={[]}
            onChange={onAssignCategories}
            onCreateCategory={onCreateCategory}
            disabled={disabled}
          />
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Remove categories</p>
          <LibraryCategoryPicker
            categories={categories}
            value={[]}
            onChange={onRemoveCategories}
            onCreateCategory={onCreateCategory}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};